import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/state/AuthContext";
import { createOperationStore, type OperationKind, type OperationStore } from "@/lib/operationReceipt";
import { createOperationWorkflow, type OperationSnapshot } from "@/lib/operationWorkflow";
import { operationApi } from "@/lib/operationApi";
import type { ApiResult } from "@/types";

const noAccount: OperationStore = {
  read: () => ({ checkpoint: null, blocked: true, notice: "请先恢复登录状态，再提交或核对原请求。" }),
  write: () => false,
};
export function useUploadOperation(kind: OperationKind, enabled = true) {
  const { user } = useAuth();
  const ownerId = user && (typeof user.id === "string" || typeof user.id === "number") ? String(user.id) : "";
  const [published, setPublished] = useState<{ ownerId: string; kind: OperationKind; snapshot: OperationSnapshot } | null>(null);
  const flow = useMemo(() => createOperationWorkflow(kind, ownerId ? createOperationStore(ownerId, kind) : noAccount,
    operationApi.lookup, (snapshot) => setPublished({ ownerId, kind, snapshot })), [ownerId, kind]);
  // A changed account must not render the previous account's key even for one paint.
  const snapshot = published?.ownerId === ownerId && published.kind === kind ? published.snapshot : flow.getSnapshot();
  const active = useRef(false);
  const currentFlow = useRef(flow);
  currentFlow.current = flow;
  useEffect(() => {
    active.current = true;
    setPublished({ ownerId, kind, snapshot: flow.getSnapshot() });
    if (enabled && ownerId) void flow.reconcile();
    return () => { active.current = false; flow.stop(); };
  }, [flow, ownerId, kind, enabled]);
  async function submit(send: (requestKey: string) => Promise<ApiResult<{ job_id?: string; status?: string; operation_receipt?: unknown }>>) {
    if (!ownerId || !enabled || !active.current || !["idle", "rejected"].includes(flow.getSnapshot().phase)) return null;
    await flow.submit(send);
    return active.current && currentFlow.current === flow && flow.getSnapshot().phase === "accepted" ? flow.getSnapshot().accepted : null;
  }
  return {
    snapshot, submit, getSnapshot: flow.getSnapshot, reconcile: flow.reconcile, beginNew: flow.beginNew,
    canSubmit: !!ownerId && enabled && ["idle", "rejected"].includes(snapshot.phase),
    working: snapshot.phase === "submitting" || snapshot.phase === "checking",
  };
}
